"use client";
import { Image } from "@/components/shared/image";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Lang } from "@/i18n/settings";

const images = ["/assets/hero-hd.jpg", "/assets/2025-hero.jpg"];

type CoverImageProps = {
  lang: Lang;
};

export default function CoverImage(props: CoverImageProps) {
  const { lang } = props;
  const isRtl = lang === "ar";

  const [emblaApi, setEmblaApi] = useState<CarouselApi>();
  const [isInitialized, setIsInitialized] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    setIsInitialized(true);
    const updateScrollButtons = () => {
      setCanScrollNext(emblaApi.canScrollNext());
      setCanScrollPrev(emblaApi.canScrollPrev());
    };

    emblaApi.on("select", updateScrollButtons);

    updateScrollButtons();

    return () => {
      emblaApi.off("select", updateScrollButtons);
    };
  }, [emblaApi]);

  const handleNext = () => {
    if (isRtl) {
      emblaApi?.scrollPrev();
    } else {
      emblaApi?.scrollNext();
    }
  };

  const handlePrev = () => {
    if (isRtl) {
      emblaApi?.scrollNext();
    } else {
      emblaApi?.scrollPrev();
    }
  };

  return (
    <AspectRatio ratio={16 / 9} className="overflow-hidden">
      <Carousel
        opts={{
          direction: isRtl ? "rtl" : "ltr",
          startIndex: images.length - 1,
        }}
        setApi={setEmblaApi}
        className="pointer-events-none"
      >
        <CarouselContent
          className={`${isInitialized ? "visible" : "invisible"} `}
        >
          {images.map((image, index) => {
            return (
              <CarouselItem key={index}>
                <Image
                  src={image}
                  width={55000}
                  height={55000}
                  alt="Image"
                  className="h-full w-full object-cover"
                />
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
      <div className="pointer-events-none absolute inset-x-0 -top-4 h-1/3 bg-gradient-to-b from-white dark:from-background"></div>
      <div className="pointer-events-none absolute inset-x-0 -bottom-4 h-1/3 bg-gradient-to-t from-white dark:from-background"></div>
      <div className="">
        <ChevronLeft
          onClick={handlePrev}
          className={`absolute top-1/2 h-6 w-6 -translate-y-1/2 sm:h-12 sm:w-12 md:h-16 md:w-16 ${
            !(isRtl ? canScrollNext : canScrollPrev)
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer"
          } left-2 sm:left-5 md:left-10`}
        />

        <ChevronRight
          onClick={handleNext}
          className={`absolute top-1/2 h-6 w-6 -translate-y-1/2 sm:h-12 sm:w-12 md:h-16 md:w-16 ${
            !(isRtl ? canScrollPrev : canScrollNext)
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer"
          } right-2 sm:right-5 md:right-10`}
        />
      </div>
    </AspectRatio>
  );
}
